/**
 * Xếp nhãn "sơ đồ lắp đặt" (GĐ2) quanh mép khung nhìn — hàm THUẦN (test được, không DOM/WebGL).
 * Chiến lược (owner chốt): ưu tiên 2 cột TRÁI/PHẢI (xếp dọc — tốt cho tên dài); khi cột quá tải thì
 * "tràn" bớt nhãn (các nhãn ở cực trên/dưới) sang HÀNG TRÊN/DƯỚI. Mỗi phía giải chồng 1D rồi kẹp biên.
 * Toạ độ tính theo pixel TRONG khung overlay (gốc trái-trên). Leader = từ mép trong nhãn → điểm neo.
 */
export interface LabelInput {
  id: number;
  ax: number; // điểm neo x (px) — tâm chi tiết đã chiếu
  ay: number; // điểm neo y (px)
  w: number; // bề rộng nhãn (px, đo thực)
  h: number; // chiều cao nhãn (px)
}

export type LabelSide = 'L' | 'R' | 'T' | 'B';

export interface LabelPlaced {
  id: number;
  side: LabelSide;
  x: number; // góc trái-trên hộp nhãn
  y: number;
  lx: number; // đầu leader phía nhãn (mép trong)
  ly: number;
  ax: number; // điểm neo (giữ nguyên để vẽ leader)
  ay: number;
}

export interface LabelViewport {
  width: number;
  height: number;
  cx: number; // tâm cụm (px) — chia L/R
  cy: number; // tâm cụm (px) — chia T/B
  margin?: number; // lề mép
  gap?: number; // khoảng cách tối thiểu giữa 2 nhãn
  colGap?: number; // khoảng cách ngang từ MÉP CỤM tới cột nhãn (để nhãn bám sát model, không ra rìa)
  rowGap?: number; // khoảng cách dọc từ mép cụm tới hàng nhãn trên/dưới
}

export function layoutLabels(items: LabelInput[], vp: LabelViewport): LabelPlaced[] {
  const margin = vp.margin ?? 8;
  const gap = vp.gap ?? 6;
  const top = margin;
  const bottom = vp.height - margin;
  const left = margin;
  const right = vp.width - margin;
  const availV = Math.max(0, bottom - top);

  // 1) gán sơ bộ L/R theo neo so với tâm cụm
  const L: LabelInput[] = [];
  const R: LabelInput[] = [];
  const T: LabelInput[] = [];
  const B: LabelInput[] = [];
  for (const it of items) (it.ax < vp.cx ? L : R).push(it);

  // 2) cột quá tải → chuyển nhãn CỰC-Y nhất sang T/B cho tới khi vừa chiều cao
  const spill = (col: LabelInput[]): void => {
    let need = col.reduce((s, it) => s + it.h + gap, 0) - gap;
    if (need <= availV) return;
    const order = [...col].sort((a, b) => Math.abs(b.ay - vp.cy) - Math.abs(a.ay - vp.cy));
    for (const it of order) {
      if (need <= availV) break;
      col.splice(col.indexOf(it), 1);
      (it.ay < vp.cy ? T : B).push(it);
      need -= it.h + gap;
    }
  };
  spill(L);
  spill(R);

  // Hộp bao CỤM (từ các neo) → đặt cột/hàng SÁT model thay vì rìa màn hình (sửa lỗi nhãn bay ra mép).
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  for (const it of items) {
    if (it.ax < minX) minX = it.ax;
    if (it.ax > maxX) maxX = it.ax;
    if (it.ay < minY) minY = it.ay;
    if (it.ay > maxY) maxY = it.ay;
  }
  const colGap = vp.colGap ?? 72;
  const rowGap = vp.rowGap ?? 48;
  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

  const out: LabelPlaced[] = [];

  // 3a) cột dọc L/R — x bám sát mép cụm (kẹp trong khung); giải chồng theo Y quanh ay
  const placeColumn = (col: LabelInput[], side: 'L' | 'R'): void => {
    const sorted = [...col].sort((a, b) => a.ay - b.ay);
    const ys: number[] = [];
    let cursor = top;
    for (const it of sorted) {
      const y = Math.max(it.ay - it.h / 2, cursor);
      ys.push(y);
      cursor = y + it.h + gap;
    }
    const over = cursor - gap - bottom;
    if (over > 0) for (let i = 0; i < ys.length; i++) ys[i] = Math.max(top, ys[i] - over);
    const rightEdge = minX - colGap; // mép phải nhãn cột TRÁI = sát mép trái cụm
    const leftEdge = maxX + colGap; // mép trái nhãn cột PHẢI = sát mép phải cụm
    sorted.forEach((it, i) => {
      const y = ys[i];
      const x = side === 'L' ? clamp(rightEdge - it.w, left, right - it.w) : clamp(leftEdge, left, right - it.w);
      out.push({ id: it.id, side, x, y, lx: side === 'L' ? x + it.w : x, ly: y + it.h / 2, ax: it.ax, ay: it.ay });
    });
  };
  placeColumn(L, 'L');
  placeColumn(R, 'R');

  // 3b) hàng ngang T/B — y bám sát mép cụm; giải chồng theo X quanh ax
  const placeRow = (row: LabelInput[], side: 'T' | 'B'): void => {
    const sorted = [...row].sort((a, b) => a.ax - b.ax);
    const xs: number[] = [];
    let cursor = left;
    for (const it of sorted) {
      const x = Math.max(it.ax - it.w / 2, cursor);
      xs.push(x);
      cursor = x + it.w + gap;
    }
    const over = cursor - gap - right;
    if (over > 0) for (let i = 0; i < xs.length; i++) xs[i] = Math.max(left, xs[i] - over);
    const botEdge = minY - rowGap; // mép dưới nhãn hàng TRÊN = sát mép trên cụm
    const topEdge = maxY + rowGap; // mép trên nhãn hàng DƯỚI = sát mép dưới cụm
    sorted.forEach((it, i) => {
      const x = xs[i];
      const y = side === 'T' ? clamp(botEdge - it.h, top, bottom - it.h) : clamp(topEdge, top, bottom - it.h);
      out.push({ id: it.id, side, x, y, lx: x + it.w / 2, ly: side === 'T' ? y + it.h : y, ax: it.ax, ay: it.ay });
    });
  };
  placeRow(T, 'T');
  placeRow(B, 'B');

  return out;
}
